import { CellContext } from '@tanstack/react-table';
import {
  Co2Icon,
  Pill,
  RiArrowRightDownLine,
  RiArrowRightUpLine,
  RiErrorWarningFill,
  Stack,
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from 'component-library';
import i18next from 'i18next';
import { FC, PropsWithChildren, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FactsByFactType } from '@/api/rest/resources/types/dashboard';
import {
  ClimateRiskRating,
  ClimateRiskRatingsByIndex,
  PlotReportFactElement,
  R1FactType,
} from '@/api/rest/resources/types/fact';
import { DraftPlot, Plot, PlotStatusEnum, PlotType } from '@/api/rest/resources/types/plot';
import { UnitEnum } from '@/api/rest/resources/types/units';
import { PlotTypeIcon } from '@/components';
import { PlotThumbnail } from '@/components/MapThumbnail/MapThumbnail';
import { useAboveGroundCarbonFacts } from '@/hooks/useAboveGroundCarbonFacts';
import { useCurrency } from '@/hooks/useCurrency';
import { getDisplayNumber, useDisplayNumber } from '@/hooks/useDisplayNumber';
import { useHiddenFactConfigurationByFactType } from '@/hooks/useWidgetConfigurationByOrganizationId';
import { isNullish } from '@/utils/booleans';
import { formatUnit, valueToTonne } from '@/utils/formatting';
import { printDDMMYYYYHHMM } from '@/utils/formatting/date';
import { getFactTrendButtonColor, getFactTrendIconColor, getFactTrendTextColor } from '@/utils/getColorForFact';
import { getIconForClimateRiskRating, getStyleForClimateRiskRating } from '@/utils/getStyleForClimateRisk';
import { squareMetersToHectares } from '@/utils/plot';
import { getColorOfPill, getTextColorOfPill } from '@/utils/plot/get-color-of-plot-status/getColorOfPlotStatus';

export type PlotRow = Plot & { facts: FactsByFactType };

const maxDisplayCropCount = 3;

export const NameCell = ({ row }: CellContext<PlotRow, unknown>) => {
  const { t } = useTranslation();
  const plot = row.original;

  const createdAt = printDDMMYYYYHHMM(plot.created_at) ?? '';
  const modifiedAt = printDDMMYYYYHHMM(plot.modified_at) ?? '';
  const analysedAt = printDDMMYYYYHHMM(plot.last_analyzed_at) ?? '';

  const editedAt =
    modifiedAt > createdAt
      ? t('shared.plots.tooltips.edited', { timestamp: modifiedAt })
      : t('shared.plots.tooltips.uploaded', { timestamp: createdAt });
  const lastActionHint =
    plot.status === PlotStatusEnum.analysed ? t('shared.plots.tooltips.analysed', { timestamp: analysedAt }) : editedAt;

  return (
    <Stack direction='row' centerMain spacing={3}>
      {'status' in plot && plot.status === PlotStatusEnum.invalid ? (
        <span className='flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-bg-light-grey'>
          <RiErrorWarningFill size={20} className='text-error' data-testid='alert-icon' />
        </span>
      ) : (
        <PlotThumbnail plot={plot} className='h-12 w-12 shrink-0 rounded-lg' />
      )}
      <Tooltip>
        <TooltipTrigger>
          <Stack direction='col' spacing={2} className='max-w-[150px] items-start'>
            <span className='max-w-full truncate'>{plot.name}</span>
          </Stack>
        </TooltipTrigger>
        <TooltipContent sideOffset={5} side='top'>
          {plot.name} ({lastActionHint})
          <TooltipArrow />
        </TooltipContent>
      </Tooltip>
    </Stack>
  );
};

export const AreaCell = ({ row }: CellContext<PlotRow, unknown> | CellContext<DraftPlot, unknown>) => {
  const plot = row.original;
  const isValid = 'status' in plot ? plot.status !== PlotStatusEnum.invalid : true;

  const areaInHectares = getDisplayNumber(squareMetersToHectares(plot.area), window.navigator.language);

  return <span className='whitespace-nowrap'>{isValid ? `${areaInHectares} ha` : '--'}</span>;
};

export const PlotTypeCell = ({ row }: CellContext<PlotRow, unknown>) => {
  const plot = row.original;
  const { t } = useTranslation();

  const plotTypeHint = useMemo(() => {
    const hasCrops = plot.type === PlotType.CROPLAND && plot.crops && plot.crops.length > 0;
    const crops = plot.crops
      .slice(0, maxDisplayCropCount)
      .map((crop) => t(`global.crops.${crop}`))
      .sort()
      .join(', ');
    return hasCrops ? t('shared.plots.tooltips.cropland', { crops }) : t(`global.plotTypes.${plot.type}`);
  }, [t, plot.type, plot.crops]);
  return (
    <Tooltip>
      <TooltipTrigger>
        <PlotTypeIcon type={plot.type} size={24} />
      </TooltipTrigger>
      <TooltipContent sideOffset={5} side='top'>
        {plotTypeHint}
        <TooltipArrow />
      </TooltipContent>
    </Tooltip>
  );
};

export const ResilienceCell = ({ row }: CellContext<PlotRow, unknown>) => {
  const { t } = useTranslation();
  const plot = row.original;
  const isFactHidden = useHiddenFactConfigurationByFactType(R1FactType.combined_uplift_pct_change);

  const fact = plot.facts[R1FactType.combined_uplift_pct_change] as PlotReportFactElement<number>;

  let formattedFact = '--';

  if (isFactHidden) return <>{formattedFact}</>;

  if (!isNullish(fact?.value)) {
    if ((fact.value as number) === 0) {
      formattedFact = t('shared.projects.project.conservation.trendLabel.stable');
    } else if ((fact.value as number) > 0) {
      formattedFact = t('shared.projects.project.conservation.trendLabel.increasing');
    } else if ((fact.value as number) < 0) {
      formattedFact = t('shared.projects.project.conservation.trendLabel.decreasing');
    }
  }
  const isOutdatedValue = formattedFact !== '--' && plot.status !== PlotStatusEnum.analysed;
  const trendIconColor = getFactTrendButtonColor(fact);
  const trendTextColor = getFactTrendTextColor(fact);

  return (
    <Pill
      data-testid='resilience-trend-cell'
      size='small'
      style={{
        backgroundColor: trendIconColor || getColorOfPill(isOutdatedValue),
        color: trendTextColor || getTextColorOfPill(plot.status),
      }}
    >
      {formattedFact}
    </Pill>
  );
};

export const NatureRiskCell = ({ row }: CellContext<PlotRow, unknown>) => {
  const plot = row.original;
  const isFactHidden = useHiddenFactConfigurationByFactType(R1FactType.combined_climate_risk);

  const fact = plot.facts[R1FactType.combined_climate_risk] as PlotReportFactElement<number>;
  const rating = ClimateRiskRatingsByIndex[fact?.value ?? 0] as ClimateRiskRating;

  const formattedFact = '--';
  const riskLevel = `${rating?.replace('_', ' ')}`;
  if (isFactHidden) return <>{formattedFact}</>;

  const riskTrendIcon = getIconForClimateRiskRating(rating);
  const style = getStyleForClimateRiskRating(rating);

  return (
    <Pill
      data-testid={`${rating}-risk-cell`}
      size='small'
      style={{
        color: style?.text,
        backgroundColor: style?.primary,
      }}
      className='typography-body2 w-fit px-4 py-2 capitalize items-baseline'
      leftAdornment={riskTrendIcon && <i className={`${riskTrendIcon} h-3 w-3`} />}
    >
      {riskLevel}
    </Pill>
  );
};

export const EcologicalIntegrityIndexCell = ({ row }: CellContext<PlotRow, unknown>) => {
  const plot = row.original;
  const isFactHidden = useHiddenFactConfigurationByFactType(R1FactType.ecological_integrity_score);

  const fact = plot.facts[R1FactType.ecological_integrity_score] as PlotReportFactElement<number>;

  const value = useDisplayNumber(fact?.value ?? 0);

  let formattedFact = '--';

  if (isFactHidden) return <>{formattedFact}</>;

  if (!isNullish(fact?.value)) {
    formattedFact = `${value}`;
  }

  return (
    <FactTooltip factDate={fact?.measured_at}>
      <span className='whitespace-nowrap typography-body1'>{formattedFact}</span>
    </FactTooltip>
  );
};

export const MonetaryRiskCell = ({ row }: CellContext<PlotRow, unknown>) => {
  const plot = row.original;
  const isFactHidden = useHiddenFactConfigurationByFactType(R1FactType.stewarded_value);
  const { convertAndFormat } = useCurrency();

  const fact = plot.facts[R1FactType.stewarded_value] as PlotReportFactElement<number>;

  let formattedFact = '--';

  if (isFactHidden) return <>{formattedFact}</>;

  if (!isNullish(fact?.value)) {
    formattedFact = convertAndFormat(fact.value);
  }

  return (
    <FactTooltip factDate={fact?.measured_at}>
      <span className='whitespace-nowrap typography-body1'>{formattedFact}</span>
    </FactTooltip>
  );
};

type FactCellProps = {
  plot: PlotRow;
  factType: R1FactType;
  unitType: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon?: any;
};
// TODO: MVP-3284 use instead of specific fact cells
// TODO: MVP-3284 add PlotListNotice as override in parent or as prop
export const FactCell: FC<FactCellProps> = ({ plot, factType, unitType, icon: Icon }) => {
  const fact = plot.facts[factType as R1FactType] as PlotReportFactElement<number>;

  const isFactHidden = useHiddenFactConfigurationByFactType(factType);
  let formattedFact = '--';

  const value = useDisplayNumber(fact?.value ?? 0);
  const unit = formatUnit(unitType as UnitEnum);

  if (isFactHidden) return <>{formattedFact}</>;

  if (!isNullish(fact?.value)) {
    formattedFact = unit ? `${value} ${unit}` : `${value}`;
  }

  const isOutdatedValue = formattedFact !== '--' && plot.status !== PlotStatusEnum.analysed;

  return (
    <FactTooltip factDate={fact?.measured_at}>
      <Pill
        data-testid={`${factType}-cell`}
        size='small'
        style={{
          backgroundColor: getColorOfPill(isOutdatedValue),
          color: getTextColorOfPill(plot.status),
        }}
        leftAdornment={Icon && <Icon size={12} color={getTextColorOfPill(plot.status)} />}
      >
        {formattedFact}
      </Pill>
    </FactTooltip>
  );
};

type FactTrendCellProps = {
  plot: PlotRow;
  factType: R1FactType;
  unitType: string;
};

export const FactTrendCell: FC<FactTrendCellProps> = ({ plot, factType, unitType }) => {
  const fact = plot.facts[factType as R1FactType] as PlotReportFactElement<number>;

  const isFactHidden = useHiddenFactConfigurationByFactType(factType);
  let formattedFact = '--';

  const value = useDisplayNumber(fact?.value ?? 0);
  const unit = formatUnit(unitType as UnitEnum);

  if (isFactHidden) return <>{formattedFact}</>;

  if (!isNullish(fact?.value)) {
    formattedFact = unit ? `${value} ${unit}` : `${value}`;
  }

  const isOutdatedValue = formattedFact !== '--' && plot.status !== PlotStatusEnum.analysed;

  const trendIcon = getTrendIcon(fact);
  const trendIconColor = getFactTrendIconColor(fact);
  const trendTextColor = getFactTrendTextColor(fact);

  return (
    <FactTooltip factDate={fact?.measured_at}>
      <Pill
        data-testid={`${factType}-trend-cell`}
        size='small'
        style={{
          backgroundColor: getColorOfPill(isOutdatedValue),
          color: trendTextColor || getTextColorOfPill(plot.status),
        }}
        leftAdornment={
          trendIcon && (
            <span style={{ color: trendIconColor || getTextColorOfPill(plot.status) }} className='h-3 w-3'>
              {trendIcon}
            </span>
          )
        }
      >
        {formattedFact}
      </Pill>
    </FactTooltip>
  );
};

export const getTrendIcon = (fact: PlotReportFactElement | undefined) => {
  if (!fact) {
    return null;
  }
  if ((fact.value as number) === 0) return null;
  if ((fact.value as number) > 0) return <RiArrowRightUpLine />;
  return <RiArrowRightDownLine />;
};

export const CarbonStorageBgCell = ({ row }: CellContext<PlotRow, unknown>) => {
  const plot = row.original;

  const carbonStorageBg = plot.facts[R1FactType.r1_carbon_storage_bg_per_ha] as PlotReportFactElement<number>;

  const isFactHidden = useHiddenFactConfigurationByFactType(R1FactType.r1_carbon_storage_bg_per_ha);
  let formattedFact = '--';

  const value = useDisplayNumber(valueToTonne(carbonStorageBg?.value, carbonStorageBg?.unit) ?? '');
  const unit = formatUnit(UnitEnum['t/ha']);

  if (isFactHidden) return formattedFact;

  if (!isNullish(carbonStorageBg?.value)) {
    formattedFact = unit ? `${value} ${unit}` : `${value}`;
  }

  const isOutdatedValue = formattedFact !== '--' && plot.status !== PlotStatusEnum.analysed;

  return (
    <FactTooltip factDate={carbonStorageBg?.measured_at}>
      <Pill
        data-testid='carbon-cell'
        size='small'
        style={{
          backgroundColor: getColorOfPill(isOutdatedValue),
          color: getTextColorOfPill(plot.status),
        }}
        leftAdornment={<Co2Icon size={12} color={getTextColorOfPill(plot.status)} />}
      >
        {formattedFact}
      </Pill>
    </FactTooltip>
  );
};

export const CarbonStorageAgCell = ({ row }: CellContext<PlotRow, unknown>) => {
  const plot = row.original;

  const facts = useAboveGroundCarbonFacts();
  let formattedFact = '--';

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const carbonStorageAg = plot.facts[facts!.perHa.latestAnalysis ?? ''] as PlotReportFactElement<number>;

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const isFactHidden = useHiddenFactConfigurationByFactType(facts!.perHa.latestAnalysis);

  const value = useDisplayNumber(valueToTonne(carbonStorageAg?.value, carbonStorageAg?.unit) ?? '');
  const unit = formatUnit(UnitEnum['t/ha']);

  if (isFactHidden) return <>{formattedFact}</>;

  if (!isNullish(carbonStorageAg?.value)) {
    formattedFact = unit ? `${value} ${unit}` : `${value}`;
  }

  const isOutdatedValue = formattedFact !== '--' && plot.status !== PlotStatusEnum.analysed;

  return (
    <FactTooltip factDate={carbonStorageAg?.measured_at}>
      <Pill
        data-testid='ag-carbon-cell'
        size='small'
        style={{
          backgroundColor: getColorOfPill(isOutdatedValue),
          color: getTextColorOfPill(plot.status),
        }}
        leftAdornment={<Co2Icon size={12} color={getTextColorOfPill(plot.status)} />}
      >
        {formattedFact}
      </Pill>
    </FactTooltip>
  );
};

type FactTooltipProps = PropsWithChildren & {
  factDate?: string | null;
};

const FactTooltip: FC<FactTooltipProps> = ({ children, factDate }) => {
  const { t } = useTranslation();

  if (factDate) {
    return (
      <Tooltip>
        <TooltipTrigger>{children}</TooltipTrigger>
        <TooltipContent sideOffset={5} side='top'>
          {t('shared.nca.landDistributionDetails.referenceDate', {
            date: new Date(factDate).toLocaleDateString(i18next.language),
          })}
          <TooltipArrow />
        </TooltipContent>
      </Tooltip>
    );
  }

  return <>{children}</>;
};
